import { resolveIsHolidayMessage } from './touchpoint-send-message.js';

/**
 * Holiday Date Calculator
 *
 * Computes send_after_date (day after the holiday) and send_before_date so
 * holiday-anchored messages only go out after the event is over.
 *
 * All returned Date objects are at local midnight (time components = 0).
 * Returns null if no specific constraint applies (send anytime in the month).
 *
 * Supports: CA (Canada), US (United States). Unknown country falls back to CA dates.
 */

/**
 * Easter Sunday using the Meeus/Jones/Butcher algorithm.
 * Returns a Date for Easter Sunday of the given year.
 */
function getEasterDate(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3=March, 4=April (1-indexed)
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day); // convert to 0-indexed month for Date
}

/**
 * Good Friday: 2 days before Easter Sunday.
 */
function getGoodFriday(year) {
  const easter = getEasterDate(year);
  const gf = new Date(easter);
  gf.setDate(gf.getDate() - 2);
  return gf;
}

/**
 * Nth occurrence of a weekday in a given month.
 * @param {number} year
 * @param {number} month - 1-indexed (1=January, 12=December)
 * @param {number} weekday - 0=Sunday, 1=Monday, ..., 6=Saturday
 * @param {number} n - 1=first, 2=second, 3=third, 4=fourth
 */
function getNthWeekdayOfMonth(year, month, weekday, n) {
  const firstDay = new Date(year, month - 1, 1);
  const firstWeekday = firstDay.getDay();
  const dayOffset = ((weekday - firstWeekday) + 7) % 7;
  const day = 1 + dayOffset + (n - 1) * 7;
  return new Date(year, month - 1, day);
}

/**
 * Last occurrence of a weekday in a given month.
 * @param {number} year
 * @param {number} month - 1-indexed
 * @param {number} weekday - 0=Sunday, 1=Monday, ..., 6=Saturday
 */
function getLastWeekdayOfMonth(year, month, weekday) {
  const lastDay = new Date(year, month, 0); // day 0 of next month = last day of this month
  const lastWeekday = lastDay.getDay();
  const daysBack = ((lastWeekday - weekday) + 7) % 7;
  return new Date(year, month - 1, lastDay.getDate() - daysBack);
}

/**
 * Victoria Day (CA): Last Monday on or before May 25.
 * Canadian statute: "Monday immediately preceding May 25."
 * If May 25 itself is a Monday, Victoria Day IS May 25.
 */
function getVictoriaDay(year) {
  const may25 = new Date(year, 4, 25);
  const dayOfWeek = may25.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  // (dayOfWeek + 6) % 7 gives days since the most recent Monday
  const daysBack = (dayOfWeek + 6) % 7;
  return new Date(year, 4, 25 - daysBack);
}

function addCalendarDay(date) {
  const d = new Date(date);
  d.setDate(d.getDate() + 1);
  return d;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatHolidayDateLabel(date) {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

/** Monday-based holidays where "long weekend" is always appropriate. */
function isMondayLongWeekendHoliday(monthNum, country) {
  const isUS = country === 'US';
  if (monthNum === 5 || monthNum === 9) return true;
  if (monthNum === 2 && !isUS) return true; // CA Family Day
  if (monthNum === 10 && !isUS) return true; // CA Thanksgiving
  return false;
}

function isWeekendDay(dayIndex) {
  return dayIndex === 0 || dayIndex === 6;
}

/**
 * Whether "weekend" / "long weekend" language fits this month's holiday in this year.
 * July 4 on a Thursday → false. Memorial Day (Monday) → true.
 *
 * @param {number} monthNum - 1–12
 * @param {number} year
 * @param {string|null} country
 * @returns {boolean}
 */
/**
 * "Long weekend" (Fri–Mon stretch) — stricter than a single Sat/Sun holiday.
 * July 4 on Saturday is a weekend day, NOT a long weekend.
 *
 * @param {number} monthNum - 1–12
 * @param {number} year
 * @param {string|null} country
 * @returns {boolean}
 */
export function isLongWeekendLanguageAllowed(monthNum, year, country) {
  if (isMondayLongWeekendHoliday(monthNum, country)) return true;

  if (monthNum !== 7) return false;

  const anchor = getHolidayAnchorDate(monthNum, year, country);
  if (!anchor) return false;

  const day = anchor.getDay();
  // Monday (Canada Day / observed) or Friday (US three-day stretch)
  return day === 1 || day === 5;
}

export function isWeekendLanguageAllowed(monthNum, year, country) {
  if (isLongWeekendLanguageAllowed(monthNum, year, country)) return true;

  const anchor = getHolidayAnchorDate(monthNum, year, country);
  if (!anchor) return false;

  if (monthNum === 7) {
    return isWeekendDay(anchor.getDay());
  }

  if (monthNum === 3 || monthNum === 4) {
    return isWeekendDay(anchor.getDay());
  }

  if (monthNum === 11 && country === 'US') {
    return false;
  }

  return isWeekendDay(anchor.getDay());
}

/**
 * Drop example lines that contradict this year's calendar (feeds the model fewer bad patterns).
 * @param {string[]} examples
 */
export function filterHolidayExamplesForYear(examples, monthNum, year, country) {
  const longOk = isLongWeekendLanguageAllowed(monthNum, year, country);
  const weekendOk = isWeekendLanguageAllowed(monthNum, year, country);
  return examples.filter((ex) => {
    if (!longOk && /\blong weekend\b/i.test(ex)) return false;
    if (!weekendOk && /\bweekend\b/i.test(ex)) return false;
    return true;
  });
}

/**
 * Tells the model the actual day-of-week for this year's holiday so it does not
 * guess "weekend" (e.g. July 4 on Thursday vs Saturday).
 *
 * @returns {string|null} Prompt paragraph or null
 */
export function getHolidayCalendarContext(monthNum, year, country) {
  const anchor = getHolidayAnchorDate(monthNum, year, country);
  if (!anchor) return null;

  const isUS = country === 'US';
  const day = anchor.getDay();
  const dayName = DAY_NAMES[day];
  const dateLabel = formatHolidayDateLabel(anchor);

  const noPerformative =
    'Never open with performative lines like "What a weekend!", "What a week!", or caption-style hype — text like you would actually type.';

  if (isMondayLongWeekendHoliday(monthNum, country)) {
    const names = {
      2: isUS ? "Presidents' Day" : 'Family Day',
      5: isUS ? 'Memorial Day' : 'Victoria Day',
      9: isUS ? 'Labor Day' : 'Labour Day',
      10: 'Thanksgiving',
    };
    return `${names[monthNum]} ${year} is ${dayName}, ${dateLabel} — a long-weekend holiday. "Long weekend" is fine. ${noPerformative}`;
  }

  if (monthNum === 7) {
    const holiday = isUS ? 'Independence Day (July 4)' : 'Canada Day (July 1)';
    const ref = isUS ? 'the 4th' : 'Canada Day';
    if (day === 1 || day === 5) {
      return `${holiday} ${year} is ${dayName}, ${dateLabel} — creates a three-day weekend. "Long weekend" is OK only after you mention ${ref} first (❌ "Hope you had a great long weekend" with no holiday name). ${noPerformative}`;
    }
    if (isWeekendDay(day)) {
      return `${holiday} ${year} is ${dayName}, ${dateLabel} — on a weekend but NOT a "long weekend" (no extra weekday off). ✅ "Happy 4th! Hope it was a good one" ❌ "long weekend" ❌ "hope you had a great weekend". ${noPerformative}`;
    }
    return `${holiday} ${year} is ${dayName}, ${dateLabel} — mid-week, NOT a weekend or long weekend. Do NOT say "weekend", "long weekend", or "hope you had a great weekend". Reference "${ref}" or ask how they're doing. ${noPerformative}`;
  }

  if (monthNum === 11 && isUS) {
    return `US Thanksgiving ${year} is ${dayName}, ${dateLabel} (Thursday). Not a summer weekend — reference Thanksgiving or a warm check-in. ${noPerformative}`;
  }

  if (monthNum === 3) {
    return `St. Patrick's Day ${year} is ${dayName}, ${dateLabel}.${isWeekendDay(day) ? ' On a weekend — keep it light.' : ' Weekday — do not call it a "weekend".'} ${noPerformative}`;
  }

  if (monthNum === 4) {
    return `Easter weekend ${year} centers on Good Friday (${dayName}, ${dateLabel}). Long-weekend framing is OK. ${noPerformative}`;
  }

  if (monthNum === 10 && isUS) {
    const halloween = new Date(year, 9, 31);
    return `Halloween ${year} is ${DAY_NAMES[halloween.getDay()]}, October 31 — this text sends in the days BEFORE Halloween, so it has NOT happened yet. Use present/upcoming framing ("Happy early Halloween!", "Halloween season — hope you're having fun with it", "Hope you've got something fun planned"). NEVER past tense like "hope Halloween was a good one". ${noPerformative}`;
  }

  return `${dateLabel}, ${year} (${dayName}). Only say "weekend" if it truly was a weekend for this date. ${noPerformative}`;
}

/**
 * Calendar date of the holiday / anchor event (before the +1 send gate).
 * @returns {Date|null}
 */
function getHolidayAnchorDate(monthNum, year, country) {
  const isUS = country === 'US';
  // Default (including CA or unknown) uses Canadian STAT calendar

  switch (monthNum) {
    case 1:
      // New Year's Day — send from Jan 1 (whole month is fine)
      return new Date(year, 0, 1);

    case 2:
      if (isUS) {
        // Valentine's Day Feb 14 comes before Presidents' Day (~Feb 17-21).
        // Using Feb 14 ensures Valentine's messages are correctly timed and
        // Presidents' Day messages are only a few days early at most.
        return new Date(year, 1, 14);
      }
      // CA: Family Day — 3rd Monday of February
      return getNthWeekdayOfMonth(year, 2, 1, 3);

    case 3:
      // St. Patrick's Day — March 17
      return new Date(year, 2, 17);

    case 4: {
      // Easter / Good Friday long weekend (date varies March–April)
      const gf = getGoodFriday(year);
      // Good Friday can fall in late March (e.g. March 29, 2024).
      // If it's in March, use April 1 so the April touchpoint still sends in April.
      if (gf.getMonth() < 3) { // getMonth() is 0-indexed; 3 = April
        return new Date(year, 3, 1);
      }
      return gf;
    }

    case 5:
      if (isUS) {
        // Memorial Day — last Monday of May
        return getLastWeekdayOfMonth(year, 5, 1);
      }
      // CA: Victoria Day — Monday on or before May 25
      return getVictoriaDay(year);

    case 6:
      // General June check-in — not solstice-gated
      return new Date(year, 5, 1);

    case 7:
      if (isUS) {
        // Independence Day — July 4
        return new Date(year, 6, 4);
      }
      // CA: Canada Day — July 1
      return new Date(year, 6, 1);

    case 8:
      // No major STAT holiday in August — send anytime from Aug 1
      return new Date(year, 7, 1);

    case 9:
      // Labour Day / Labor Day — 1st Monday of September (same CA and US)
      return getNthWeekdayOfMonth(year, 9, 1, 1);

    case 10:
      if (isUS) {
        // Halloween build-up: send from Oct 25 so the message feels timely
        return new Date(year, 9, 25);
      }
      // CA: Thanksgiving — 2nd Monday of October
      return getNthWeekdayOfMonth(year, 10, 1, 2);

    case 11:
      if (isUS) {
        // US Thanksgiving — 4th Thursday of November
        return getNthWeekdayOfMonth(year, 11, 4, 4);
      }
      // CA: Remembrance Day — November 11
      return new Date(year, 10, 11);

    case 12:
      // Christmas / Holiday Season — send from Dec 1 (whole month feels festive)
      return new Date(year, 11, 1);

    default:
      return null;
  }
}

/**
 * Returns the earliest date touchpoints may send.
 *
 * Always the calendar day AFTER the holiday anchor — e.g. US Memorial Day Monday
 * → sends start Tuesday. Sender uses send_after_date <= today.
 *
 * @param {number} monthNum - 1–12
 * @param {number} year - full four-digit year, e.g. 2026
 * @param {string|null} country - 'CA', 'US', or null/undefined (defaults to CA)
 * @returns {Date|null} First day sending may begin, or null
 */
export function getSendAfterDate(monthNum, year, country) {
  const anchor = getHolidayAnchorDate(monthNum, year, country);
  if (!anchor) return null;
  return addCalendarDay(anchor);
}

/**
 * Formats a Date (or null) as an ISO date string 'YYYY-MM-DD' for Supabase.
 * Returns null if the date is null/undefined.
 *
 * @param {Date|null} date
 * @returns {string|null}
 */
export function formatSendAfterDate(date) {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Days after send_after_date when a specific holiday hook still feels natural.
 * NULL months use full-month sends (no send_before cap).
 */
const FRESHNESS_DAYS_AFTER_HOOK = {
  2: 2,
  3: 2,
  4: 3,
  5: 3,
  6: 5,
  7: 3,
  9: 3,
  10: 3,
  11: 3,
};

/** Months where the hook works all month — no send_before cap. */
const FULL_MONTH_HOOK_MONTHS = new Set([1, 12]);

/**
 * Last day a holiday-specific touchpoint should send before swapping to a timeless fallback.
 * @param {number} monthNum - 1–12
 * @param {number} year
 * @param {string|null} country
 * @returns {Date|null}
 */
export function getSendBeforeDate(monthNum, year, country) {
  if (FULL_MONTH_HOOK_MONTHS.has(monthNum)) return null;

  if (monthNum === 8) return null;

  // US Halloween is a pre-holiday build-up — send through Halloween night.
  if (monthNum === 10 && country === 'US') return new Date(year, 9, 31);

  const freshnessDays = FRESHNESS_DAYS_AFTER_HOOK[monthNum];
  if (!freshnessDays) return null;

  const sendAfter = getSendAfterDate(monthNum, year, country);
  if (!sendAfter) return null;

  const before = new Date(sendAfter);
  before.setDate(before.getDate() + freshnessDays);

  const monthEnd = new Date(year, monthNum, 0);
  return before > monthEnd ? monthEnd : before;
}

/**
 * @param {Date|null} date
 * @returns {string|null}
 */
export function formatSendBeforeDate(date) {
  return formatSendAfterDate(date);
}

/**
 * Resolve send dates from DB or compute from month + country when missing.
 * @param {string} monthYear - YYYY-MM
 * @param {string|null} country
 * @param {string|null} storedAfter
 * @param {string|null} storedBefore
 */
export function resolveTouchpointSendDates(monthYear, country, storedAfter, storedBefore) {
  if (storedAfter || storedBefore) {
    return { sendAfterDate: storedAfter ?? null, sendBeforeDate: storedBefore ?? null };
  }
  const [year, monthNum] = monthYear.split('-').map(Number);
  return {
    sendAfterDate: formatSendAfterDate(getSendAfterDate(monthNum, year, country)),
    sendBeforeDate: formatSendBeforeDate(getSendBeforeDate(monthNum, year, country)),
  };
}

function formatShortDate(isoDate) {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Human label for send timing shown in the UI.
 * @param {string|null} messageText - When omitted or non-holiday, shows full-month spread.
 * @param {boolean|null} isHolidayMessage - AI-declared at generation (preferred over text inference).
 * @returns {{ label: string, status: 'waiting'|'active'|'open'|'past'|'spread'|'none', detail?: string }|null}
 */
export function describeTouchpointSendWindow(sendAfterDate, sendBeforeDate, todayIso, messageText = null, isHolidayMessage = null) {
  const today = todayIso || new Date().toISOString().substring(0, 10);

  const holidayAnchored = messageText
    ? resolveIsHolidayMessage(messageText, isHolidayMessage)
    : isHolidayMessage === true;

  if (messageText && !holidayAnchored) {
    return {
      label: 'Spreads through the month',
      status: 'spread',
      detail: 'Non-holiday message — not limited to the holiday week',
    };
  }

  if (!sendAfterDate && !sendBeforeDate) {
    return holidayAnchored
      ? null
      : { label: 'Spreads through the month', status: 'spread' };
  }

  if (sendAfterDate && sendBeforeDate) {
    const holidayLabel = `All sends this month: ${formatShortDate(sendAfterDate)} – ${formatShortDate(sendBeforeDate)}`;
    const detail = 'Holiday message — entire month’s texts go out in this short window only (split evenly on your send schedule)';
    if (today < sendAfterDate) {
      return { label: `${holidayLabel} · starts soon`, status: 'waiting', detail };
    }
    if (today > sendBeforeDate) {
      return { label: `${holidayLabel} · complete`, status: 'past', detail: 'This month’s touchpoint finished in the holiday week' };
    }
    return { label: holidayLabel, status: 'active', detail };
  }

  if (sendAfterDate) {
    const label = `Holiday copy from ${formatShortDate(sendAfterDate)}`;
    if (today < sendAfterDate) return { label: `${label} · starts soon`, status: 'waiting' };
    return { label, status: 'open', detail: 'Spreads through month end after holiday week' };
  }

  const label = `Holiday copy through ${formatShortDate(sendBeforeDate)}`;
  if (today > sendBeforeDate) {
    return { label: `${label} · ended`, status: 'past', detail: 'General check-ins for the rest of the month' };
  }
  return { label, status: 'active', detail: 'Then spreads through month end' };
}
